<template>
  <v-app id="app" :class="appClass">
    <c-progress-overlay
      :pending="wholePending"
      :transition="false"
      color="gray"
    />
    <v-layout v-if="!wholePending">
      <the-navigation
        id="main-navigation"
        v-if="currentUser && shownHeader"
      />
      <v-main id="main-content">
        <active-broadcast-message />
        <router-view :key="routeViewKey" />
      </v-main>
    </v-layout>
    <the-sidebar />
    <the-modals />
    <the-popups />
  </v-app>
</template>

<script>
import { isEmpty } from 'lodash';

import { SOCKET_URL, SOCKET_ROOMS, LOCAL_STORAGE_ACCESS_TOKEN_KEY, LOCAL_STORAGE_WARNING_POPUP_KEY } from '@/config';
import { EXCLUDED_SERVER_ERROR_STATUSES, RESPONSE_STATUSES, ROUTES_NAMES } from '@/constants';

import Socket from '@/plugins/socket/services/socket';

import localStorageService from '@/services/local-storage';

import { reloadPageWithTrailingSlashes } from '@/helpers/url';

import { authMixin } from '@/mixins/auth';
import { systemMixin } from '@/mixins/system';
import { shownHeaderMixin } from '@/mixins/shown-header';
import { entitiesInfoMixin } from '@/mixins/entities/info';
import { entitiesUserMixin } from '@/mixins/entities/user';
import { entitiesTemplateVarsMixin } from '@/mixins/entities/template-vars';
import { vuetifyCustomIconsRegisterMixin } from '@/mixins/vuetify/custom-icons/register';
import { appRemediationInstructionExecutionsPopupsMixin } from '@/mixins/app/remediation-instruction-executions-popups';

import TheNavigation from '@/components/layout/navigation/the-navigation.vue';
import ActiveBroadcastMessage from '@/components/layout/broadcast-message/active-broadcast-message.vue';

import '@/assets/styles/main.scss';

export default {
  components: {
    TheNavigation,
    ActiveBroadcastMessage,
  },
  mixins: [
    authMixin,
    systemMixin,
    shownHeaderMixin,
    entitiesInfoMixin,
    entitiesUserMixin,
    entitiesTemplateVarsMixin,
    vuetifyCustomIconsRegisterMixin,
    appRemediationInstructionExecutionsPopupsMixin,
  ],
  data() {
    return {
      currentUserLocalPending: true,
      appInfoLocalPending: true,
    };
  },
  computed: {
    appClass() {
      return {
        'v-app--side-bar-groups': this.isShownGroupsSideBar,
        'v-app--disabled-transitions': this.disabledTransitions,
      };
    },
    wholePending() {
      return this.currentUserLocalPending || this.appInfoLocalPending || this.templateVarsPending;
    },

    routeViewKey() {
      switch (this.$route.name) {
        case ROUTES_NAMES.view:
          return this.$route.path;

        case ROUTES_NAMES.notifications:
          return this.$route.name;

        default:
          return this.$route.fullPath;
      }
    },
  },
  watch: {
    templateVars: 'setTitle',
    currentUser: 'setTitle',
  },
  beforeCreate() {
    reloadPageWithTrailingSlashes();
  },
  created() {
    this.socketConnectWithErrorHandling();
  },
  async mounted() {
    this.showLocalStorageWarningPopupMessage();

    await Promise.all([
      this.fetchCurrentUserWithErrorHandling(),
      this.fetchTemplateVarsWithErrorHandling(),
      this.fetchIconsWithRegistering(),
    ]);

    await this.fetchAppInfoWithErrorHandling();

    this.registerCurrentUserOnceWatcher();

    if (!this.isLoggedIn) {
      this.setTheme(this.defaultColorTheme);
    }
  },
  methods: {
    showLocalStorageWarningPopupMessage() {
      const text = localStorageService.pop(LOCAL_STORAGE_WARNING_POPUP_KEY);

      if (text) {
        this.$popups.warning({ text, autoClose: false });
      }
    },

    registerCurrentUserOnceWatcher() {
      const unwatch = this.$watch('currentUser', async (currentUser) => {
        if (!isEmpty(currentUser)) {
          this.$socket.authenticate(localStorageService.get(LOCAL_STORAGE_ACCESS_TOKEN_KEY));

          this.setTheme(currentUser.ui_theme_colors);
          this.filesAccess();

          if (this.isProVersion) {
            this.runExecutionsPopups();
          }

          unwatch();
        }
      });
    },

    socketConnectWithErrorHandling() {
      try {
        this.$socket
          .connect(SOCKET_URL)
          .on(Socket.EVENTS_TYPES.error, this.socketErrorHandler)
          .on(Socket.EVENTS_TYPES.networkError, this.socketConnectionFailureHandler);
      } catch (err) {
        this.$popups.error({
          text: this.$t('errors.socketConnectionProblem'),
          autoClose: false,
        });

        console.error(err);
      }
    },

    socketErrorHandler({ message, error } = {}) {
      if (message) {
        const { room } = error ?? {};
        const statusCode = +message;

        if (statusCode === RESPONSE_STATUSES.unauthorized || message === Socket.ERROR_MESSAGES.authenticationFailed) {
          localStorageService.set(LOCAL_STORAGE_WARNING_POPUP_KEY, this.$t('warnings.authTokenExpired'));
          this.logout();

          return;
        }

        if (room === SOCKET_ROOMS.llmChat || statusCode === RESPONSE_STATUSES.forbidden) {
          return;
        }

        const textKey = {
          [RESPONSE_STATUSES.notFound]: 'errors.socketConnectionRoomNotFound',
          [RESPONSE_STATUSES.badRequest]: 'errors.socketConnectionRoomBadRequest',
          [RESPONSE_STATUSES.internalServerError]: 'errors.socketConnectionRoomInternalServerError',
        }[statusCode];

        const text = this.$te(textKey) ? this.$t(textKey) : message;

        this.$popups.error({ text });
      }
    },

    socketConnectionFailureHandler() {
      this.$popups.error({
        text: this.$t('errors.socketConnectionProblem'),
        autoClose: false,
      });
    },

    async fetchCurrentUserWithErrorHandling() {
      try {
        this.currentUserLocalPending = true;

        await this.fetchCurrentUser();
      } catch (err) {
        if (!EXCLUDED_SERVER_ERROR_STATUSES.includes(err.status)) {
          this.$router.push({ name: ROUTES_NAMES.error });
        }

        console.error(err);
      } finally {
        this.currentUserLocalPending = false;
      }
    },

    async fetchAppInfoWithErrorHandling() {
      try {
        this.appInfoLocalPending = true;
        await this.fetchAppInfo();

        this.setSystemData({
          timezone: this.timezone,
        });

        this.setTitle();
      } catch (err) {
        if (!EXCLUDED_SERVER_ERROR_STATUSES.includes(err.status)) {
          this.$router.push({
            name: ROUTES_NAMES.error,
          });
        }

        console.error(err);
      } finally {
        this.appInfoLocalPending = false;
      }
    },

    async fetchTemplateVarsWithErrorHandling() {
      try {
        await this.fetchTemplateVars();
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style lang="scss">
#app {
  &.-fullscreen {
    width: 100%;

    #main-navigation {
      display: none;
    }

    #main-content {
      padding: 0 !important;
    }
  }
}
</style>
